import { useEffect, useMemo, useRef } from 'react';
import { FormProvider, useForm, useFormContext, useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { dsClass } from '../../../common/constants/dsClasses.js';
import {
  addressConfirmationErrorMsg,
  costCenterAndReferenceVariableMsg,
  costCenterMsg,
  customerMsg,
  methodOfDeliveryMsg,
  notMandatoryMsg,
  referenceMsg,
  t,
} from '../../../common/i18n/index.js';
import { ApproverContactDropdown } from '../../../common/react-hook-form/fields/ApproverContactDropdown.js';
import { CostCenter } from '../../../common/react-hook-form/fields/CostCenter.js';
import { Reference } from '../../../common/react-hook-form/fields/Reference.js';
import { rhfErrorsToStringMap } from '../../../common/react-hook-form/utils.js';
import { hasEmployeePayables, showEppDeviceChangeStep } from '../../../common/utils/checkoutUtils.js';
import { canApproveDeviceOrder } from '../../../common/utils/contactUtils';
import { convertStringMapToCommonErrors } from '../../../common/utils/errorUtils.js';
import { deepEqual } from '../../../common/utils/objectUtils.js';
import { getContactInfo } from '../../../common/utils/stateUtils.js';
import { capitalize } from '../../../common/utils/stringUtils.js';
import { CompanyInfoResponse } from '../../../generated/api/models.js';
import { useAuth } from '../../../public/site/AuthProvider.js';
import { useSessionData } from '../../../public/site/SessionDataProvider.js';
import { processDeliveryDetails } from '../../../selfservice/actions/index.js';
import { DeviceCheckoutDeliveryDetails } from '../../DeviceCheckoutDeliveryDetails/DeviceCheckoutDeliveryDetails.js';
import { Loading } from '../../Loading/index.js';
import type { SubmitOrderProps } from '../../../common/types/checkout.js';
import type { DeviceChangeRequest } from '../../../common/types/device.js';
import type { CommonError } from '../../../common/types/errors.js';
import type { Contact, ContactPerson } from '../../../generated/api/models.js';
import type { AuthenticatedUser } from '../../../public/site/authState';
import type { ActionsHistory, State } from '../../../selfservice/common/store.js';
import type { AddressConfirmationRef } from '../../DeviceCheckoutDeliveryDetails/DeviceCheckoutAddressConfirmation.js';
import type { DeviceCheckoutDeliveryDetailsType } from '../../DeviceCheckoutDeliveryDetails/DeviceCheckoutDeliveryDetailsUtils.js';
import type { CheckoutStepItem } from '../EoeCheckoutSteps.js';
import type { FieldErrors } from 'react-hook-form';

export interface DeliveryDetailsStepContentProps {
  deliveryDetails: DeviceCheckoutDeliveryDetailsType;
  setShouldScrollToError: (value: boolean, errors?: CommonError[]) => void;
  contactPerson?: ContactPerson;
  setContactPerson: (contactPerson: ContactPerson) => void;
  contactPersonValidationErrors: { [s: string]: string };
  setContactPersonValidationErrors: (contactPersonValidationErrors: { [s: string]: string }) => void;
  approverContact?: string;
  setApproverContact: (approverContact: string) => void;
  approverContactValidationErrors: { [s: string]: string };
  setApproverContactValidationErrors: (approverContactValidationErrors: { [s: string]: string }) => void;
  shouldScrollToError: boolean;
  deviceChangeRequest?: DeviceChangeRequest;
  setDeliveryDetailsObj: (updatedDeliveryDetails: DeviceCheckoutDeliveryDetailsType) => void;
  submitOrder: (submitOrderProps: SubmitOrderProps) => void;
  setRefCallback?: (key: string, ref: HTMLElement | null) => void;
  hasPreInstallation?: boolean;
  contacts?: Contact[];
}

export interface DeliveryDetailsFormValues extends ContactPerson {
  costCenter?: string;
  employeeNumber?: string;
  approverContact?: string;
}

const CostCenterAndReferenceNumberDetails = ({
  costCenterMandatory,
  setContactPerson,
  setContactPersonValidationErrors,
  setRefCallback,
}: {
  costCenterMandatory: boolean;
  setContactPerson: (contactPerson: ContactPerson) => void;
  setContactPersonValidationErrors: (contactPersonValidationErrors: { [s: string]: string }) => void;
  setRefCallback?: (key: string, ref: HTMLElement | null) => void;
}) => {
  const {
    formState: { errors },
  } = useFormContext<DeliveryDetailsFormValues>();
  const values = useWatch<DeliveryDetailsFormValues>();

  useEffect(() => {
    setContactPerson(values as ContactPerson);
    setContactPersonValidationErrors(rhfErrorsToStringMap(errors));
  }, [errors, values, setContactPerson, setContactPersonValidationErrors]);

  return (
    <div className={`of-checkout__costCenterAndReferenceNumber ${dsClass.MARGIN_TOP_3}`}>
      <div
        id="costCenter"
        ref={(element: HTMLElement | null) => {
          setRefCallback?.('costCenter', element);
        }}
      />
      <CostCenter
        name="costCenter"
        label={`${t.L2OG(costCenterMsg)}${!costCenterMandatory ? ' ' + t.NFRH(notMandatoryMsg) : ''}`}
        placeholder={t.L2OG(costCenterMsg)}
        required={costCenterMandatory}
      />
      <div
        id="employeeNumber"
        ref={(element: HTMLElement | null) => {
          setRefCallback?.('employeeNumber', element);
        }}
      />
      <Reference label={`${t.LVRN(referenceMsg)} ${t.NFRH(notMandatoryMsg)}`} placeholder={t.LVRN(referenceMsg)} />
    </div>
  );
};

const EmployeeApproverContactSelection = ({
  deliveryDetailsStepContentProps,
  approverContacts,
}: {
  deliveryDetailsStepContentProps: DeliveryDetailsStepContentProps;
  approverContacts: Contact[];
}) => {
  const { authenticatedUser } = useAuth();
  const companyInfo = useSessionData().companyInfo;
  const customerType = companyInfo?.customerType;
  const internalCustomer = customerType === CompanyInfoResponse.CustomerTypeEnum.INTERNAL_CUSTOMERS;
  const {
    contacts,
    setApproverContact,
    setApproverContactValidationErrors,
    setContactPerson,
    setContactPersonValidationErrors,
    setRefCallback,
  } = deliveryDetailsStepContentProps;

  return (
    <div className="of-device-checkout--order-delivery-details">
      <div>
        <h4 className={`${dsClass.MARGIN_0} ${dsClass.PADDING_BOTTOM_1}`}>{t.IY5V(customerMsg)}</h4>
        <div>{authenticatedUser && `${authenticatedUser.firstName} ${authenticatedUser.lastName || ''}`}</div>
        <h4 className={`${dsClass.MARGIN_0} ${dsClass.PADDING_BOTTOM_1} ${dsClass.PADDING_TOP_3}`}>
          {t.L2OG(costCenterMsg)}
        </h4>
        <div>{t.GUAE(costCenterAndReferenceVariableMsg, capitalize(t.ACH1('device')))}</div>

        <CostCenterAndReferenceNumberDetails
          costCenterMandatory={internalCustomer}
          setContactPerson={setContactPerson}
          setContactPersonValidationErrors={setContactPersonValidationErrors}
          setRefCallback={setRefCallback}
        />
      </div>
      {contacts && (
        <div className="of-device-checkout--approver-details">
          <h4 className={`${dsClass.MARGIN_0} ${dsClass.PADDING_BOTTOM_1}`}>{t.KDIV('Order details')}</h4>
          <div
            id="approverContact"
            ref={(element: HTMLElement | null) => {
              setRefCallback?.('approverContact', element);
            }}
          />
          <ApproverContactDropdown
            contacts={approverContacts}
            setApproverContact={setApproverContact}
            setApproverContactValidationErrors={setApproverContactValidationErrors}
          />
        </div>
      )}
    </div>
  );
};

const CheckoutDeliveryDetails = (props: DeliveryDetailsStepContentProps) => {
  const dispatch = useDispatch();
  const {
    deliveryDetails,
    approverContact: approverContactObj,
    setShouldScrollToError,
    deviceChangeRequest,
    setDeliveryDetailsObj,
    submitOrder,
    setRefCallback,
    hasPreInstallation,
  } = props;
  const companyInfo = useSessionData().companyInfo;
  const customerType = companyInfo?.customerType;
  const cartItems = useSelector((state: State) => state.deviceCheckout?.cartItems || [], deepEqual);
  const { authenticatedUser } = useAuth();
  const isEmployeePayable = hasEmployeePayables(cartItems, deviceChangeRequest);

  const methods = useFormContext<DeliveryDetailsFormValues>();
  const addressConfirmationRef = useRef<AddressConfirmationRef>(null);

  const validateAddressAndCollectErrors = async (
    formErrors: FieldErrors<DeliveryDetailsFormValues> = {}
  ): Promise<{ addressValid: boolean; allErrors: Record<string, string> }> => {
    const addressValid = addressConfirmationRef.current
      ? await addressConfirmationRef.current.triggerValidation()
      : true;

    const allErrors = rhfErrorsToStringMap(formErrors);
    if (!addressValid) {
      allErrors.addressChecked = t.WTWB(addressConfirmationErrorMsg);
    }
    return { addressValid, allErrors };
  };

  const onSaveDeliveryDetailsAndNext = async (updatedDeliveryDetails: DeviceCheckoutDeliveryDetailsType) => {
    const onValid = async (data: DeliveryDetailsFormValues) => {
      const { addressValid, allErrors } = await validateAddressAndCollectErrors();

      if (addressValid) {
        dispatch(
          processDeliveryDetails(
            updatedDeliveryDetails,
            undefined,
            customerType,
            data as ContactPerson,
            approverContactObj,
            true,
            isEmployeePayable,
            true
          )
        );
      } else {
        setShouldScrollToError(true, convertStringMapToCommonErrors(allErrors));
      }
    };

    const onInvalid = async (formErrors: FieldErrors<DeliveryDetailsFormValues>) => {
      const { allErrors } = await validateAddressAndCollectErrors(formErrors);
      setShouldScrollToError(true, convertStringMapToCommonErrors(allErrors));
    };

    await methods.handleSubmit(onValid, onInvalid)();
  };

  const onSaveDeliveryDetailsToState = (
    updatedDeliveryDetails: DeviceCheckoutDeliveryDetailsType,
    validationErrors?: CommonError[]
  ) => {
    dispatch(processDeliveryDetails(updatedDeliveryDetails, validationErrors));
    setDeliveryDetailsObj(updatedDeliveryDetails);
  };

  const employeeSubmitOrder = async (updatedDeliveryDetails: DeviceCheckoutDeliveryDetailsType) => {
    const onValid = async () => {
      const { addressValid, allErrors } = await validateAddressAndCollectErrors();

      if (addressValid) {
        submitOrder({ updatedDeliveryDetails });
      } else {
        setShouldScrollToError(true, convertStringMapToCommonErrors(allErrors));
      }
    };

    const onInvalid = async (formErrors: FieldErrors<DeliveryDetailsFormValues>) => {
      const { allErrors } = await validateAddressAndCollectErrors(formErrors);
      setShouldScrollToError(true, convertStringMapToCommonErrors(allErrors));
    };

    await methods.handleSubmit(onValid, onInvalid)();
  };

  return (
    <DeviceCheckoutDeliveryDetails
      deliveryDetails={deliveryDetails}
      saveDeliveryDetailsAndNext={onSaveDeliveryDetailsAndNext}
      saveDeliveryDetailsToState={onSaveDeliveryDetailsToState}
      showConfirmAddress={true}
      footerNote={
        showEppDeviceChangeStep(cartItems, authenticatedUser)
          ? t.BHMU('On weekdays orders done before noon will mainly get shipped during next weekday delivery.')
          : undefined
      }
      onSubmitOrder={!isEmployeePayable ? employeeSubmitOrder : undefined}
      hasPreInstallation={hasPreInstallation}
      setRefCallback={setRefCallback}
      addressConfirmationRef={addressConfirmationRef}
    />
  );
};

const getDefaultValues = (user?: AuthenticatedUser & ActionsHistory): DeliveryDetailsFormValues => {
  if (user) {
    const contactInfo: ContactPerson = getContactInfo(user);
    return {
      ...contactInfo,
      costCenter: contactInfo.costCenter || '',
      employeeNumber: contactInfo.employeeNumber || '',
      approverContact: '',
    };
  }
  // User may be empty when user info is still loading.
  return { email: '', firstName: '', lastName: '', phoneNumber: '' };
};

export const DeliveryDetailsStepContent = (props: DeliveryDetailsStepContentProps) => {
  const { authenticatedUser } = useAuth();
  const { contacts } = props;
  const methods = useForm<DeliveryDetailsFormValues>({
    defaultValues: getDefaultValues(authenticatedUser),
    mode: 'all',
  });

  const filteredContacts = useMemo(
    () =>
      (contacts || [])
        .filter(contact => contact.contactId !== authenticatedUser?.contact?.contactId)
        .filter(contact => canApproveDeviceOrder(contact)),
    [contacts, authenticatedUser]
  );

  return authenticatedUser?.isFetching || !filteredContacts ? (
    <Loading />
  ) : (
    <div>
      <FormProvider {...methods}>
        <form noValidate>
          {contacts && (
            <EmployeeApproverContactSelection
              deliveryDetailsStepContentProps={props}
              approverContacts={filteredContacts}
            />
          )}
          <CheckoutDeliveryDetails {...props} />
        </form>
      </FormProvider>
    </div>
  );
};

export const deliveryDetailsStep = (props: DeliveryDetailsStepContentProps): CheckoutStepItem => ({
  id: 'deliveryDetailsStep',
  content: <DeliveryDetailsStepContent {...props} />,
  name: t.G0QN(methodOfDeliveryMsg),
});
